import React, { useEffect, useState } from "react";
import { Drawer, Typography, Tag } from "antd";
import {
  CalendarOutlined,
  ExpandOutlined,
  EditOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  TagOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SimpleTagManager } from "./SimpleTagManager";
import { useTags } from "hooks";

const { Title, Text } = Typography;

const IterationDetailsDrawer = ({
  open,
  onClose,
  iteration,
  project,
  tasks,
  source = "iteration-details",
  onTagsUpdate,
}) => {
  const { tags: allTags, loading } = useTags();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllMeta, setShowAllMeta] = useState(false);
  const [localTags, setLocalTags] = useState(iteration?.tags || []);

  useEffect(() => {
    setLocalTags(iteration?.tags || []);
  }, [iteration]);

  if (!iteration) return null;

  const totalTasks = tasks.length;
  const completed = tasks.filter((t) => t.status === "COMPLETED").length;
  const inProgress = tasks.filter((t) => t.status === "IN_PROGRESS").length;
  const openTasks = tasks.filter((t) => t.status === "OPEN").length;

  const today = dayjs();

  const duration =
    iteration.startDate && iteration.endDate
      ? dayjs(iteration.endDate).diff(dayjs(iteration.startDate), "day") + 1
      : null;

  const daysRemaining = iteration.endDate
    ? dayjs(iteration.endDate).diff(today, "day")
    : null;

  const metaItems = [
    {
      icon: <TagOutlined />,
      label: "Type",
      value: <Text>{iteration.type}</Text>,
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Key",
      value: <Text>{iteration.key}</Text>,
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Target Points",
      value: <Text>{iteration.targetPoints ?? "—"}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Start Date",
      value: (
        <Text>
          {iteration.startDate
            ? dayjs(iteration.startDate).format("MMM D, YYYY")
            : "—"}
        </Text>
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "End Date",
      value: (
        <Text>
          {iteration.endDate
            ? dayjs(iteration.endDate).format("MMM D, YYYY")
            : "—"}
        </Text>
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Duration",
      value: <Text>{duration ? `${duration} days` : "—"}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Days Remaining",
      value: (
        <Text
          style={{
            color:
              daysRemaining < 0
                ? "#ef4444"
                : daysRemaining <= 7
                ? "#f59e0b"
                : "#111827",
            fontWeight: 500,
          }}
        >
          {daysRemaining != null ? `${daysRemaining} days` : "—"}
        </Text>
      ),
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Total Tasks",
      value: <Text>{totalTasks}</Text>,
    },
    {
      icon: <CheckCircleOutlined />,
      label: "Completed",
      value: <Text>{completed}</Text>,
    },
    {
      icon: <CheckCircleOutlined />,
      label: "In Progress",
      value: <Text>{inProgress}</Text>,
    },
    {
      icon: <CheckCircleOutlined />,
      label: "Open",
      value: <Text>{openTasks}</Text>,
    }
  ];

  const filteredMetaItems =
    source === "project-details"
      ? metaItems.filter(
          (item) =>
            !["Total Tasks", "Completed", "In Progress", "Open"].includes(
              item.label,
            ),
        )
      : metaItems;

  const visibleCount = 4;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "100%" : 520}
      zIndex={99999}
      title={null}
      closable={false}
    >
      <div
        style={{
          maxWidth: isExpanded ? 700 : "100%",
          margin: isExpanded ? "0 auto" : 0,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Breadcrumb Context */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span>{project?.name}</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {iteration.name}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              style={{ cursor: "pointer" }}
              onClick={() => setIsExpanded((prev) => !prev)}
            />
          </div>
        </div>

        {/* title */}
        <Title level={4} style={{ marginBottom: 16 }}>
          {iteration.name}
        </Title>

        {/* meta Section */}
        <div style={{ marginBottom: 20 }}>
          {filteredMetaItems.map((item, index) => (
            <MetaRow
              key={index}
              icon={item.icon}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>

        <MetaRow
          icon={<TagOutlined />}
          label="Tags"
          value={
            <SimpleTagManager
              entityType="iteration"
              entityId={iteration.id}
              projectId={project.id}
              currentTags={localTags}
              availableTags={allTags}
              onChange={(updatedTags) => {
                setLocalTags(updatedTags);
                onTagsUpdate?.(iteration.id, updatedTags);
              }}
            />
          }
        />

        {/* description */}
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6b7280",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            <FileTextOutlined />
            <span>Description</span>
          </div>

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              background: "#fafafa",
            }}
          >
            <Text style={{ fontSize: 13 }}>
              {iteration.description || "No description provided"}
            </Text>
          </div>
        </div>

        {/* progress Indicator */}
        {source === "iteration-details" && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 13,
                color: "#6b7280",
                marginBottom: 8,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>Progress</span>
              <span>
                {totalTasks > 0
                  ? `${Math.round((completed / totalTasks) * 100)}%`
                  : "0%"}
              </span>
            </div>

            <div
              style={{
                height: 8,
                background: "#e5e7eb",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${
                    totalTasks > 0 ? (completed / totalTasks) * 100 : 0
                  }%`,
                  background: "#22c55e",
                  height: "100%",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

/* Meta Row */
const MetaRow = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
        flex: 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

export default IterationDetailsDrawer;
